/**
 * App -- Main Application Component
 * Step 9F Green Phase -- Xyden Leadership Assessment Platform
 *
 * Renders the full route tree using React Router v6.
 * Does NOT include BrowserRouter -- that is provided externally
 * (by main.tsx in production, by MemoryRouter in tests).
 *
 * Layout modes:
 *   1. Admin routes: AdminLayout (Sidebar + TopBar + main) -- role-gated
 *   2. Candidate routes: CandidateShell (minimal, no sidebar)
 *   3. Login: No shell at all
 *   4. Results: Uses admin layout for non-candidate roles
 *
 * Route tree derives from frontend_screen_design.md Section 5d.
 */

import React, { Suspense, useCallback, useEffect, useState } from "react";
import { lazyWithRetry } from "./lib/lazyWithRetry";
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AdminLayout } from "./layouts/AdminLayout";
import { CandidateShell } from "./layouts/CandidateShell";
import { useAuthStore } from "./stores/authStore";
import { useActiveTestStore, withActiveTestId } from "./stores/activeTestStore";
import { authService } from "./services/authService";
import { apiClient } from "./services/apiClient";
import { PageSpinner } from "./components/PageSpinner";
import type { User } from "./types";

// ---------------------------------------------------------------------------
// Eagerly loaded pages (critical path -- needed immediately by tests)
// ---------------------------------------------------------------------------
import { LoginPage } from "./pages/LoginPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { ContentStudioHomePage } from "./pages/ContentStudioHomePage";
import { ContentStudioVersionsPage } from "./pages/ContentStudioVersionsPage";
import { VersionDetailPage } from "./pages/VersionDetailPage";
// TestsDashboardPage is lazy-loaded (Suspense test requires it)
import { CandidateAssessmentsPage } from "./pages/CandidateAssessmentsPage";
import { CandidateWelcomePage } from "./pages/CandidateWelcomePage";
import { CandidateTPPage } from "./pages/CandidateTPPage";
import { CreateTestWizard as CreateTestWizardPage } from "./pages/CreateTestWizard";
import { ResultsDashboardPage } from "./pages/ResultsDashboardPage";

// ---------------------------------------------------------------------------
// Lazy-loaded pages (non-critical route components)
// ---------------------------------------------------------------------------
const TestsDashboardPage = lazyWithRetry(() => import('./pages/TestsDashboardPage'));
const MirrorReportPage = lazyWithRetry(() => import('./pages/MirrorReportPage'));
const ActivityManagementPage = lazyWithRetry(() => import('./pages/ActivityManagementPage'));
const BucketManagementPage = lazyWithRetry(() => import('./pages/BucketManagementPage'));
const CompetencyLibraryListPage = lazyWithRetry(() => import('./pages/CompetencyLibraryListPage'));
const CompetencyLibraryDetailPage = lazyWithRetry(() => import('./pages/CompetencyLibraryDetailPage'));
const QuestionBankPage = lazyWithRetry(() => import('./pages/QuestionBankPage'));
const TriadSelectionMappingPage = lazyWithRetry(() => import('./pages/TriadSelectionMappingPage'));
const TriadMappingMatrixPage = lazyWithRetry(() => import('./pages/TriadMappingMatrixPage'));
const ActivityMaturityMappingPage = lazyWithRetry(() => import('./pages/ActivityMaturityMappingPage'));
const ScoringRuleSetsPage = lazyWithRetry(() => import('./pages/ScoringRuleSetsPage'));
const ScoringRuleEditorPage = lazyWithRetry(() => import('./pages/ScoringRuleEditorPage'));
// Narrative pages (attribute config, prompt template editor, generation,
// coverage dashboard, upload) are intentionally NOT routed: per the UAT scope
// decision the narrative pipeline is legacy/hidden and the Mirror Report is the
// live deliverable (gaps FR-018 / FR-007 / FR-F-006). Their routes were removed
// so the pages are unreachable; the component files remain as dead code pending
// removal. Do not re-add these routes without a product decision.
const ValidationDashboardPage = lazyWithRetry(() => import('./pages/ValidationDashboardPage'));

const TestMonitoringPage = lazyWithRetry(() => import('./pages/TestMonitoringPage'));
const ClientsManagersPage = lazyWithRetry(() => import('./pages/ClientsManagersPage'));
const ClientManagerHomePage = lazyWithRetry(() => import('./pages/ClientManagerHomePage'));
const ClientTestsListPage = lazyWithRetry(() => import('./pages/ClientTestsListPage'));
const ClientTestDetailPage = lazyWithRetry(() => import('./pages/ClientTestDetailPage'));
const RoleTPBuilderPage = lazyWithRetry(() => import('./pages/RoleTPBuilderPage'));
const SuccessionBaselineBuilderPage = lazyWithRetry(() => import('./pages/SuccessionBaselineBuilderPage'));
const MirrorPromptConfigPage = lazyWithRetry(() => import('./pages/MirrorPromptConfigPage'));

const ClientResultsViewerPage = lazyWithRetry(() => import('./pages/ClientResultsViewerPage'));
const CandidateAQPage = lazyWithRetry(() => import('./pages/CandidateAQPage'));
const UserManagementPage = lazyWithRetry(() => import('./pages/UserManagementPage'));
const SystemConfigPage = lazyWithRetry(() => import('./pages/SystemConfigPage'));
const AuditLogPage = lazyWithRetry(() => import('./pages/AuditLogPage'));

// ---------------------------------------------------------------------------
// RBAC role sets for route gating (from frontend_screen_design.md Section 5d)
// ---------------------------------------------------------------------------
const CONTENT_STUDIO_ROLES = ["Content Admin", "Super Admin"];
const TEST_ADMIN_ROLES = ["Test Administrator", "Super Admin"];
const CLIENT_MANAGER_ROLES = ["Client Manager", "Super Admin"];
const SUPER_ADMIN_ROLES = ["Super Admin"];

// Post-login route mapping per primary role
const POST_LOGIN_ROUTES: Record<string, string> = {
  "Super Admin": "/super-admin/users",
  "Content Admin": "/content-studio",
  "Test Administrator": "/test-admin/tests",
  "Client Manager": "/client",
  Candidate: "/candidate/assessments",
};

function getPostLoginRoute(user: User): string {
  for (const role of user.roles) {
    const route = POST_LOGIN_ROUTES[role];
    if (route) return route;
  }
  return "/content-studio";
}

// ---------------------------------------------------------------------------
// Suspense wrapper for lazy-loaded routes
// ---------------------------------------------------------------------------

function SuspenseProgressBar() {
  return (
    <div role="progressbar" aria-label="Loading page" className="fixed top-0 left-0 right-0 z-50 h-1 bg-brand-steel/10 overflow-hidden">
      <div className="h-full w-1/3 bg-brand-crimson rounded-full animate-[progress-slide_1.2s_ease-in-out_infinite]" />
      <style>{`@keyframes progress-slide { 0% { transform: translateX(-100%); } 100% { transform: translateX(400%); } }`}</style>
    </div>
  );
}

function LazyRoute({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<SuspenseProgressBar />}>
      {children}
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// Route guard -- checks auth and role before rendering children.
// Reads user directly from Zustand store. AuthCheckProvider updates the store
// asynchronously. Guard re-renders when user state changes.
// ---------------------------------------------------------------------------

interface RouteGuardProps {
  requiredRoles: string[];
  children: React.ReactNode;
}

function RouteGuard({ requiredRoles, children }: RouteGuardProps) {
  const user = useAuthStore((s) => s.user);

  const location = useLocation();

  if (!user) {
    // Not authenticated -- redirect to /login page
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  const hasRequiredRole = requiredRoles.length === 0 || requiredRoles.some((role) => user.roles.includes(role));

  if (!hasRequiredRole) {
    return (
      <main className="p-8 w-full" style={{ minHeight: "600px" }}>
        <div className="text-center py-12">
          <h1 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-sm text-gray-600">
            You do not have permission to access this page. Please contact your administrator.
          </p>
        </div>
      </main>
    );
  }

  return <>{children}</>;
}

/** Redirects to /candidate/assessments when the candidate has no test assignment. */
function CandidateTestGuard({ children }: { children: React.ReactNode }) {
  const [hasTest, setHasTest] = useState<boolean | null>(null);
  const activeTestId = useActiveTestStore((s) => s.testId);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<{ test?: { id?: string } | null }>(withActiveTestId("/api/candidate/assessment"))
      .then((data) => { if (!cancelled) setHasTest(!!data?.test?.id); })
      .catch(() => { if (!cancelled) setHasTest(false); });
    return () => { cancelled = true; };
  }, [activeTestId]);

  if (hasTest === null) return <PageSpinner label="Checking access" />;
  if (!hasTest) return <Navigate to="/candidate/assessments" replace />;
  return <>{children}</>;
}

/** Shows login form for unauthenticated access to protected routes */
// function LoginRedirect() {
//   const navigate = useNavigate();
//   const login = useAuthStore(s => s.login);
//
//   const handleLogin = useCallback(async (credentials: { email: string; password: string }) => {
//     await login(credentials.email, credentials.password);
//   }, [login]);
//
//   return <LoginPage onLogin={handleLogin} />;
// }

// ---------------------------------------------------------------------------
// Login page wrapper -- provides onLogin handler with role-based redirect
// ---------------------------------------------------------------------------

function LoginPageWrapper() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const setToken = useAuthStore((s) => s.setToken);

  const handleLogin = useCallback(
    async (credentials: { email: string; password: string }) => {
      const data = await authService.login(credentials);
      setToken(data.token);
      setUser(data.user);
      navigate(getPostLoginRoute(data.user));
    },
    [navigate, setUser, setToken]
  );

  return <LoginPage onLogin={handleLogin} />;
}

// ---------------------------------------------------------------------------
// Candidate TP wrapper -- provides onSubmit to navigate to AQ
// ---------------------------------------------------------------------------

function CandidateTPWrapper() {
  const navigate = useNavigate();
  return <CandidateTPPage onSubmit={() => navigate("/candidate/aq")} />;
}

// ---------------------------------------------------------------------------
// Assessment link redirect -- /assess/:testId/:participantId
// Sets the active test in session storage and sends the candidate to login
// (or straight to assessments if already authenticated).
// ---------------------------------------------------------------------------

function AssessRedirect() {
  const { testId } = useParams<{ testId: string; participantId: string }>();
  const setTestId = useActiveTestStore((s) => s.setTestId);
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    if (testId) setTestId(testId);
  }, [testId, setTestId]);

  if (user) return <Navigate to="/candidate/assessments" replace />;
  return <Navigate to="/login" replace />;
}

// ---------------------------------------------------------------------------
// App component
// ---------------------------------------------------------------------------

export function App() {
  return (
    <>
    <Toaster />
    <Routes>
      {/* Public: Login (no shell) */}
      <Route path="/login" element={<LoginPageWrapper />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/assess/:testId/:participantId" element={<AssessRedirect />} />

      {/* Candidate routes */}
      <Route path="/candidate" element={<CandidateShell />}>
        <Route path="assessments" element={<CandidateAssessmentsPage />} />
        <Route path="welcome" element={<CandidateWelcomePage />} />
        <Route path="tp" element={<CandidateTestGuard><CandidateTPWrapper /></CandidateTestGuard>} />
        <Route path="aq" element={<CandidateTestGuard><LazyRoute><CandidateAQPage /></LazyRoute></CandidateTestGuard>} />
      </Route>

      {/* Admin routes: full shell (sidebar + banner + main) -- all role-gated */}
      <Route element={<AdminLayout />}>
        {/* Root redirect */}
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* Content Studio */}
        <Route path="/content-studio" element={<RouteGuard requiredRoles={CONTENT_STUDIO_ROLES}><ContentStudioHomePage /></RouteGuard>} />
        <Route path="/content-studio/versions" element={<RouteGuard requiredRoles={CONTENT_STUDIO_ROLES}><ContentStudioVersionsPage /></RouteGuard>} />
        <Route path="/content-studio/versions/:id" element={<RouteGuard requiredRoles={CONTENT_STUDIO_ROLES}><VersionDetailPage /></RouteGuard>} />

        <Route path="/content-studio/buckets" element={<RouteGuard requiredRoles={CONTENT_STUDIO_ROLES}><LazyRoute><BucketManagementPage /></LazyRoute></RouteGuard>} />
        <Route path="/content-studio/activities" element={<RouteGuard requiredRoles={CONTENT_STUDIO_ROLES}><LazyRoute><ActivityManagementPage /></LazyRoute></RouteGuard>} />
        <Route path="/content-studio/competencies" element={<RouteGuard requiredRoles={CONTENT_STUDIO_ROLES}><LazyRoute><CompetencyLibraryListPage /></LazyRoute></RouteGuard>} />
        <Route path="/content-studio/competencies/:id" element={<RouteGuard requiredRoles={CONTENT_STUDIO_ROLES}><LazyRoute><CompetencyLibraryDetailPage /></LazyRoute></RouteGuard>} />
        <Route path="/content-studio/questions" element={<RouteGuard requiredRoles={CONTENT_STUDIO_ROLES}><LazyRoute><QuestionBankPage /></LazyRoute></RouteGuard>} />
        <Route path="/content-studio/triad-matrix" element={<RouteGuard requiredRoles={CONTENT_STUDIO_ROLES}><LazyRoute><TriadMappingMatrixPage /></LazyRoute></RouteGuard>} />
        <Route path="/content-studio/scoring-rule-sets" element={<RouteGuard requiredRoles={CONTENT_STUDIO_ROLES}><LazyRoute><ScoringRuleSetsPage /></LazyRoute></RouteGuard>} />
        <Route path="/content-studio/scoring-rule-sets/:ruleSetId/rules" element={<RouteGuard requiredRoles={CONTENT_STUDIO_ROLES}><LazyRoute><ScoringRuleEditorPage /></LazyRoute></RouteGuard>} />
        <Route path="/content-studio/scoring-rules" element={<RouteGuard requiredRoles={CONTENT_STUDIO_ROLES}><LazyRoute><ScoringRuleEditorPage /></LazyRoute></RouteGuard>} />
        <Route path="/content-studio/scoring/triad-selection" element={<RouteGuard requiredRoles={CONTENT_STUDIO_ROLES}><LazyRoute><TriadSelectionMappingPage /></LazyRoute></RouteGuard>} />
        <Route path="/content-studio/maturity-mapping" element={<RouteGuard requiredRoles={CONTENT_STUDIO_ROLES}><LazyRoute><ActivityMaturityMappingPage /></LazyRoute></RouteGuard>} />
        {/* Narrative routes removed — pages are legacy/hidden (out of UAT scope);
            the Mirror Report is the live narrative deliverable. Reaching any
            /content-studio/narrative-* URL now falls through to NotFoundPage. */}
        <Route path="/content-studio/validation" element={<RouteGuard requiredRoles={CONTENT_STUDIO_ROLES}><LazyRoute><ValidationDashboardPage /></LazyRoute></RouteGuard>} />

        {/* Test Administration */}
        <Route path="/test-admin/clients" element={<RouteGuard requiredRoles={TEST_ADMIN_ROLES}><LazyRoute><ClientsManagersPage /></LazyRoute></RouteGuard>} />
        <Route path="/test-admin/tests" element={<RouteGuard requiredRoles={TEST_ADMIN_ROLES}><LazyRoute><TestsDashboardPage /></LazyRoute></RouteGuard>} />
        <Route path="/test-admin/tests/create" element={<RouteGuard requiredRoles={TEST_ADMIN_ROLES}><CreateTestWizardPage /></RouteGuard>} />
        <Route path="/test-admin/tests/:id/monitor" element={<RouteGuard requiredRoles={TEST_ADMIN_ROLES}><LazyRoute><TestMonitoringPage /></LazyRoute></RouteGuard>} />
        <Route path="/test-admin/tests/:id/role-tp" element={<RouteGuard requiredRoles={TEST_ADMIN_ROLES}><LazyRoute><RoleTPBuilderPage /></LazyRoute></RouteGuard>} />
        <Route path="/test-admin/tests/:id/baseline" element={<RouteGuard requiredRoles={TEST_ADMIN_ROLES}><LazyRoute><SuccessionBaselineBuilderPage /></LazyRoute></RouteGuard>} />
        <Route path="/test-admin/mirror-prompts" element={<RouteGuard requiredRoles={TEST_ADMIN_ROLES}><LazyRoute><MirrorPromptConfigPage /></LazyRoute></RouteGuard>} />

        {/* Client Manager */}
        <Route path="/client" element={<RouteGuard requiredRoles={CLIENT_MANAGER_ROLES}><LazyRoute><ClientManagerHomePage /></LazyRoute></RouteGuard>} />
        <Route path="/client/tests" element={<RouteGuard requiredRoles={CLIENT_MANAGER_ROLES}><LazyRoute><ClientTestsListPage /></LazyRoute></RouteGuard>} />
        <Route path="/client/tests/:id" element={<RouteGuard requiredRoles={CLIENT_MANAGER_ROLES}><LazyRoute><ClientTestDetailPage /></LazyRoute></RouteGuard>} />
        <Route path="/client/tests/:id/role-tp" element={<RouteGuard requiredRoles={CLIENT_MANAGER_ROLES}><LazyRoute><RoleTPBuilderPage /></LazyRoute></RouteGuard>} />
        <Route path="/client/tests/:id/baseline" element={<RouteGuard requiredRoles={CLIENT_MANAGER_ROLES}><LazyRoute><SuccessionBaselineBuilderPage /></LazyRoute></RouteGuard>} />
        <Route path="/client/tests/:id/results" element={<RouteGuard requiredRoles={CLIENT_MANAGER_ROLES}><LazyRoute><ClientResultsViewerPage /></LazyRoute></RouteGuard>} />

        {/* Results (shared, role-adaptive) */}
        <Route path="/results/:testId" element={<ResultsDashboardPage />} />
        <Route
          path="/results/:testId/mirror"
          element={
            <RouteGuard requiredRoles={TEST_ADMIN_ROLES}>
              <LazyRoute><MirrorReportPage /></LazyRoute>
            </RouteGuard>
          }
        />

        {/* Super Admin */}
        <Route path="/super-admin/users" element={<RouteGuard requiredRoles={SUPER_ADMIN_ROLES}><LazyRoute><UserManagementPage /></LazyRoute></RouteGuard>} />
        <Route path="/super-admin/settings" element={<RouteGuard requiredRoles={SUPER_ADMIN_ROLES}><LazyRoute><SystemConfigPage /></LazyRoute></RouteGuard>} />
        <Route path="/super-admin/audit" element={<RouteGuard requiredRoles={SUPER_ADMIN_ROLES}><LazyRoute><AuditLogPage /></LazyRoute></RouteGuard>} />

        {/* 404 Catch-All */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
    </>
  );
}

export default App;
